import { useState, useEffect } from "react";
import { supabase, BRAND, btn, errorBanner, segWrap, segBtn, INK, TEXT, SURFACE, WHITE, LINE, FAINT } from "./constants";
import { fillQuestionTemplate } from "./referenceQuestionsDefault";
import { useIsMobile } from "./lib/responsive";
import Logo from "./Logo";
import { PageLoader } from "./LogoLoader";

const ReferenceFormView = ({ token }) => {
  // Abre en el idioma en que se pidió la referencia (?lang= del enlace, 24/10);
  // los enlaces anteriores no lo llevan y abren en español, como antes
  const [lang, setLang] = useState(() => { try { return new URLSearchParams(window.location.search).get("lang") === "en" ? "en" : "es"; } catch (e) { return "es"; } });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tokenData, setTokenData] = useState(null);
  const [candidateName, setCandidateName] = useState("");
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const isMobile = useIsMobile();

  // Inject DM Sans font into document head when rendering standalone
  useEffect(() => {
    if (document.querySelector('link[data-rutment-font]')) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&family=JetBrains+Mono:wght@700&display=swap";
    link.setAttribute("data-rutment-font", "true");
    document.head.appendChild(link);
    document.body.style.fontFamily = "'DM Sans', sans-serif";
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!token) { setError("missing-token"); setLoading(false); return; }
      // Una función comprueba el token y devuelve solo lo que el formulario
      // necesita, nombre del candidato incluido. Antes se leía la tabla entera
      // con una regla abierta, y el nombre salía en blanco en las peticiones de
      // Rutment porque un visitante no podía leer `candidates` (06/10).
      const { data: t, error: tErr } = await supabase.rpc("get_reference_form", { p_token: token });
      if (tErr || !t || t.error) { setError("not-found"); setLoading(false); return; }
      if (t.status !== "pending") { setError("already-submitted"); setTokenData(t); setLoading(false); return; }

      setCandidateName(t.candidate_name || "");
      setTokenData(t);
      setLoading(false);
    };
    load();
  }, [token]);

  const handleSubmit = async () => {
    if (!tokenData || submitting) return;
    // Sin campos obligatorios: quien responde está haciendo un favor y ocho
    // preguntas con asterisco es la forma más rápida de que abandone. Lo único
    // que se comprueba es que no se envíe completamente en blanco.
    const anyAnswer = Object.values(responses).some(v => (v || "").trim());
    if (!anyAnswer) { setError("missing-fields"); return; }
    setSubmitting(true);
    setError("");
    const { data: res, error: updErr } = await supabase.rpc("submit_reference", { p_token: token, p_responses: responses });
    if (updErr || !res?.ok) {
      console.error(updErr || res);
      setError("submit-failed");
      setSubmitting(false);
      return;
    }
    // Aviso solo para las peticiones de Rutment. Las del portal las pidió el
    // candidato: son suyas, vienen verificadas y no hay nada que aprobar.
    if (!tokenData.requested_by_candidate) {
    fetch("/api/send-email", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: "reference_submitted",
        // Solo el token: la ruta saca de la base a quién y de quién (23/10)
        token,
      })
    }).catch(e => console.error(e));
    }
    setSubmitted(true);
    setSubmitting(false);
  };

  if (loading) {
    return <PageLoader minHeight="100vh" label={lang === "es" ? "Cargando..." : "Loading..."} />;
  }

  if (error === "not-found" || error === "missing-token") {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: isMobile ? 24 : 40 }}>
        <div style={{ maxWidth: 480, textAlign: "center" }}>
          <div style={{ marginBottom: 20 }}><Logo size={18} /></div>
          <h1 style={{ fontSize: isMobile ? 22 : 22, fontWeight: 800, marginBottom: 12, color: INK }}>{lang === "es" ? "Enlace no válido" : "Link not valid"}</h1>
          <p style={{ color: TEXT, fontSize: 13, lineHeight: 1.6 }}>{lang === "es" ? "El enlace de la solicitud de referencia no es válido o ha caducado. Pide un enlace nuevo a quien te lo envió." : "The reference request link is invalid or has expired. Please contact the recruiter for a new link."}</p>
        </div>
      </div>
    );
  }

  if (error === "already-submitted" || submitted) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: isMobile ? 24 : 40 }}>
        <div style={{ maxWidth: 480, textAlign: "center" }}>
          <div style={{ marginBottom: 20 }}><Logo size={18} /></div>
          <h1 style={{ fontSize: isMobile ? 22 : 22, fontWeight: 800, marginBottom: 12, color: INK }}>{lang === "es" ? "¡Gracias!" : "Thank you!"}</h1>
          <p style={{ color: TEXT, fontSize: 13, lineHeight: 1.6 }}>
            {tokenData?.requested_by_candidate
              ? (lang === "es"
                  ? "Hemos recibido tus respuestas y ya forman parte del perfil de esta persona. Gracias por tu tiempo."
                  : "We've received your answers and they're now part of this person's profile. Thanks for your time.")
              : (lang === "es"
                  ? "Hemos recibido tus respuestas. El equipo las revisará antes de incorporarlas al proceso. Gracias por tu tiempo."
                  : "We've received your answers. The team will review them before adding them to the process. Thanks for your time.")}
          </p>
        </div>
      </div>
    );
  }

  const questions = tokenData?.questions || [];

  return (
    <div style={{ minHeight: "100vh", background: SURFACE, padding: isMobile ? "16px 16px calc(32px + env(safe-area-inset-bottom))" : "40px 20px" }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: isMobile ? 16 : 28 }}>
          <Logo size={20} />
          <div style={{ ...segWrap }}>
            {["es", "en"].map(l => (
              <button key={l} onClick={() => setLang(l)}
                style={{ ...segBtn(lang === l), padding: "5px 10px", fontSize: 11, textTransform: "uppercase", ...(isMobile ? { minWidth: 44, minHeight: 36, fontSize: 12 } : {}) }}>
                {l}
              </button>
            ))}
          </div>
        </div>

        {/* Title */}
        <div style={{ background: WHITE, borderRadius: 14, border: `1.5px solid ${LINE}`, padding: isMobile ? 16 : 22, marginBottom: 16 }}>
          <h1 style={{ fontSize: isMobile ? 22 : 22, fontWeight: 800, color: INK, margin: 0, marginBottom: 8, letterSpacing: -0.3 }}>
            {lang === "es" ? "Referencia profesional" : "Professional reference"}
          </h1>
          <p style={{ color: TEXT, fontSize: 13, lineHeight: 1.6, margin: 0 }}>
            {lang === "es"
              ? <>Has sido contactado para dar una referencia profesional sobre <strong>{candidateName}</strong>. Tus respuestas son confidenciales y se utilizarán para apoyar el proceso de evaluación. Responde solo lo que puedas: ninguna pregunta es obligatoria. Gracias por tu tiempo.</>
              : <>You've been contacted to provide a professional reference about <strong>{candidateName}</strong>. Your answers are confidential and will be used to support the evaluation process. Answer only what you can — no question is required. Thanks for your time.</>}
          </p>
        </div>

        {/* Questions */}
        <div style={{ background: WHITE, borderRadius: 14, border: `1.5px solid ${LINE}`, padding: isMobile ? 16 : 22, marginBottom: 16 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
            {questions.map((q, idx) => (
              <div key={q.id}>
                <label style={{ display: "block", fontSize: isMobile ? 15 : 13, fontWeight: 700, color: INK, marginBottom: 8, lineHeight: isMobile ? 1.5 : undefined }}>
                  {idx + 1}. {fillQuestionTemplate(q.text, candidateName)}
                </label>
                <textarea
                  value={responses[q.id] || ""}
                  onChange={e => setResponses(prev => ({ ...prev, [q.id]: e.target.value }))}
                  rows={4}
                  style={{ width: "100%", padding: "12px 14px", border: `1.5px solid ${LINE}`, borderRadius: 10, fontSize: isMobile ? 16 : 13, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box", outline: "none", lineHeight: 1.6 }}
                  onFocus={e => e.target.style.borderColor = BRAND}
                  onBlur={e => e.target.style.borderColor = LINE}
                />
              </div>
            ))}
          </div>

          {error === "missing-fields" && (
            <div style={{ ...errorBanner, marginTop: 16 }}>
              {lang === "es" ? "Responde al menos una pregunta antes de enviar." : "Please answer at least one question before submitting."}
            </div>
          )}
          {error === "submit-failed" && (
            <div style={{ ...errorBanner, marginTop: 16 }}>
              {lang === "es" ? "Error al enviar. Inténtalo de nuevo." : "Submit failed. Try again."}
            </div>
          )}
        </div>

        {/* Submit */}
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button onClick={handleSubmit} disabled={submitting}
            style={{ ...btn.primary, cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.6 : 1, ...(isMobile ? { width: "100%", padding: "14px 20px", fontSize: 15 } : {}) }}>
            {submitting ? (lang === "es" ? "Enviando..." : "Submitting...") : (lang === "es" ? "Enviar respuestas" : "Submit answers")}
          </button>
        </div>

        <div style={{ textAlign: "center", color: FAINT, fontSize: 11, marginTop: 32 }}>© 2026 Rutment</div>
      </div>
    </div>
  );
};

export default ReferenceFormView;
